import React, { useEffect, useState } from 'react';
import { FileSpreadsheet, FileText, RefreshCw } from 'lucide-react';
import { fetchFutureArrivalsReport, FutureArrivalsReportData, TodayArrivalsReportData } from '../../../services/citasService.ts';
import { fetchSucursales, Sucursal } from '../../../services/pos/sucursalService.ts';
import TodayArrivalsReport from './TodayArrivalsReport.tsx';
import { exportArrivalsReportExcel, exportArrivalsReportPdf } from '../../../utils/arrivalsReportExports.ts';

const localDateKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const TodayArrivalsReportPage: React.FC = () => {
    const [fechaDesde, setFechaDesde] = useState(localDateKey);
    const [fechaHasta, setFechaHasta] = useState('');
    const [report, setReport] = useState<FutureArrivalsReportData | null>(null);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [sucursalId, setSucursalId] = useState<number | undefined>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [exportError, setExportError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const selectedBranchName = sucursales.find(item => item.id === sucursalId)?.nombre;
    const reportBranchName = selectedBranchName || (sucursales.length === 1 ? sucursales[0].nombre : undefined);
    const reportForExport: TodayArrivalsReportData | null = report ? {
        fecha: `${report.fecha_desde}_a_${report.fecha_hasta}`,
        advertencia: report.rango_limitado
            ? 'REPORTE LIMITADO A 730 DÍAS. EXISTEN RESERVAS POSTERIORES NO INCLUIDAS.'
            : undefined,
        total_reservas: report.total_ocurrencias,
        total_clientes: report.total_clientes,
        reservas: report.dias.flatMap(day => day.reservas.map(reservation => ({
            ...reservation,
            fecha_ocurrencia: day.fecha,
        }))),
    } : null;

    const downloadPdf = () => {
        if (!reportForExport) return;
        setExportError('');
        try {
            exportArrivalsReportPdf(reportForExport, reportBranchName);
        } catch {
            setExportError('No se pudo generar el PDF. Intenta nuevamente.');
        }
    };

    const downloadExcel = async () => {
        if (!reportForExport) return;
        setExportError('');
        try {
            await exportArrivalsReportExcel(reportForExport, reportBranchName);
        } catch {
            setExportError('No se pudo generar el Excel. Intenta nuevamente.');
        }
    };

    useEffect(() => {
        fetchSucursales()
            .then(items => setSucursales(items.filter(item => item.activo)))
            .catch(() => setSucursales([]));
    }, []);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        fetchFutureArrivalsReport(fechaDesde, sucursalId, fechaHasta || undefined)
            .then(data => { if (active) setReport(data); })
            .catch(() => { if (active) { setReport(null); setError('No se pudo cargar el reporte.'); } })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [fechaDesde, fechaHasta, sucursalId, refresh]);

    return (
        <main style={{ minHeight: '100vh', background: '#fff', padding: 'clamp(24px,3vw,38px) clamp(18px,4vw,42px)', fontFamily: "'DM Sans', sans-serif" }}>
            <div style={{ maxWidth: 1240, margin: '0 auto' }}>
                <header style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                        <div><p style={{ color: '#007FC8', fontSize: '.7rem', fontWeight: 900, letterSpacing: '.1em', textTransform: 'uppercase', margin: '0 0 6px' }}>Administración · Reportes</p><h1 style={{ margin: 0, fontSize: 'clamp(1.45rem,3vw,1.9rem)', color: '#0f172a', fontWeight: 900, letterSpacing:'-.025em' }}>Próximas llegadas de clientes</h1><p style={{ color: '#64748b', margin: '6px 0 0', fontSize:'.84rem' }}>Agenda efectiva por fecha, sucursal y días configurados en cada reserva.</p><div style={{ display:'flex', gap:5, marginTop:11 }}>{['#FF2052','#007FC8','#EC8400','#00DBB9'].map((color,index)=><span key={color} style={{ width:index===0?24:8, height:3, borderRadius:3, background:color }}/>)}</div></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <button aria-label="Descargar PDF" disabled={!report || loading} onClick={downloadPdf} style={{ ...exportButtonStyle, color: '#9B004A', background: '#fff1f4', opacity: !report || loading ? .5 : 1 }}><FileText size={16} /> PDF</button>
                            <button aria-label="Descargar Excel" disabled={!report || loading} onClick={downloadExcel} style={{ ...exportButtonStyle, color: '#047857', background: '#ecfdf5', opacity: !report || loading ? .5 : 1 }}><FileSpreadsheet size={16} /> Excel</button>
                        </div>
                    </div>
                </header>
                <section aria-labelledby="arrivals-query-title" style={{ background:'#fff', border:'1.5px solid #eef2f7', borderRadius:18, boxShadow:'0 8px 30px rgba(15,23,42,.045)', overflow:'hidden', marginBottom:22 }}>
                    <div style={{ height:4, background:'linear-gradient(90deg,#FF2052,#007FC8,#EC8400,#00DBB9)' }}/>
                    <div style={{ padding:'15px 18px 6px' }}><h2 id="arrivals-query-title" style={{ margin:0, color:'#0f172a', fontSize:'.95rem', fontWeight:900 }}>Consulta de agenda</h2><p style={{ margin:'3px 0 0', color:'#94a3b8', fontSize:'.72rem' }}>Selecciona el período y la sucursal que deseas revisar.</p></div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', alignItems:'end', gap:12, padding:'12px 18px 16px' }}>
                        <label style={fieldLabelStyle}>Desde<input aria-label="Fecha inicial del reporte" type="date" value={fechaDesde} onChange={event => setFechaDesde(event.target.value)} style={fieldStyle}/></label>
                        <label style={fieldLabelStyle}>Hasta (opcional)<input aria-label="Fecha final del reporte" type="date" min={fechaDesde} value={fechaHasta} onChange={event => setFechaHasta(event.target.value)} style={fieldStyle}/></label>
                        <label style={fieldLabelStyle}>Sucursal<select aria-label="Sucursal del reporte" value={sucursalId || ''} onChange={event => setSucursalId(event.target.value ? Number(event.target.value) : undefined)} style={{ ...fieldStyle, background:'#fff' }}><option value="">Todas las sucursales autorizadas</option>{sucursales.map(sucursal => <option key={sucursal.id} value={sucursal.id}>{sucursal.nombre}</option>)}</select></label>
                        <button aria-label="Actualizar reporte" onClick={() => setRefresh(value => value + 1)} style={{ ...buttonStyle, width:'auto', padding:'0 15px' }}><RefreshCw size={15}/> Actualizar</button>
                    </div>
                    <div style={{ padding:'10px 18px', borderTop:'1px solid #f1f5f9', background:'#fafcff', fontSize:'.7rem', color:'#64748b', fontWeight:600 }}>La fecha final es opcional. Por seguridad se muestran hasta 24 meses; para consultar períodos posteriores, avanza la fecha inicial.</div>
                </section>
                {exportError && <div role="alert" style={{ ...stateStyle, padding: 14, marginBottom: 18, color: '#b91c1c' }}>{exportError}</div>}
                {loading ? <div role="status" style={stateStyle}>Cargando reporte…</div> : error ? <div role="alert" style={stateStyle}>{error}</div> : report && (
                    <div style={{ display: 'grid', gap: 18 }}>
                        {report.rango_limitado && <div role="note" style={{ padding:'10px 14px', borderRadius:12, background:'#fff7ed', border:'1px solid #fed7aa', color:'#9a3412', fontSize:'.78rem', fontWeight:700 }}>
                            Existen reservas posteriores a este período. Avance la fecha inicial para consultar las siguientes ocurrencias.
                        </div>}
                        {report.dias.length ? report.dias.map(day => <TodayArrivalsReport key={day.fecha} report={day} />) : (
                            <div style={stateStyle}>No existen llegadas futuras para los filtros seleccionados.</div>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
};

const buttonStyle: React.CSSProperties = { height: 38, width: 38, border: 0, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#007FC8', background: '#eef8ff', fontWeight: 800, cursor: 'pointer' };
const exportButtonStyle: React.CSSProperties = { height: 40, border: 0, borderRadius: 10, padding: '0 14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontWeight: 900, cursor: 'pointer' };
const stateStyle: React.CSSProperties = { background: '#fff', border: '1.5px solid #f1f5f9', borderRadius: 18, padding: 48, textAlign: 'center', color: '#64748b', fontWeight: 800 };
const fieldLabelStyle: React.CSSProperties = { display:'grid', gap:5, color:'#475569', fontSize:'.68rem', fontWeight:850, textTransform:'uppercase', letterSpacing:'.04em' };
const fieldStyle: React.CSSProperties = { width:'100%', height:40, border:'1.5px solid #e2e8f0', borderRadius:10, padding:'0 11px', color:'#334155', fontSize:'.8rem' };

export default TodayArrivalsReportPage;
